/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AccountCheckDto,
  ApiErrorDto,
  CreateSocialAccountDto,
  SocialAccountDto,
  UpdateSocialAccountDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The accounts the workspace publishes to, by platform.
 */
export const listSocialAccounts = (id: string, signal?: AbortSignal) => {
  return customInstance<SocialAccountDto[]>({
    url: `/api/v1/workspaces/${id}/social-accounts`,
    method: 'GET',
    signal,
  });
};

export const getListSocialAccountsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/social-accounts`] as const;
};

export const getListSocialAccountsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSocialAccounts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSocialAccounts>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSocialAccountsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialAccounts>>> = ({
    signal,
  }) => listSocialAccounts(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listSocialAccounts>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListSocialAccountsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSocialAccounts>>
>;
export type ListSocialAccountsQueryError = ErrorType<ApiErrorDto>;

export function useListSocialAccounts<
  TData = Awaited<ReturnType<typeof listSocialAccounts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSocialAccounts>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSocialAccounts>>,
          TError,
          Awaited<ReturnType<typeof listSocialAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSocialAccounts<
  TData = Awaited<ReturnType<typeof listSocialAccounts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSocialAccounts>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSocialAccounts>>,
          TError,
          Awaited<ReturnType<typeof listSocialAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSocialAccounts<
  TData = Awaited<ReturnType<typeof listSocialAccounts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSocialAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The accounts the workspace publishes to, by platform.
 */

export function useListSocialAccounts<
  TData = Awaited<ReturnType<typeof listSocialAccounts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSocialAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSocialAccountsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add an account for the brand or one of its personas.
 */
export const createSocialAccount = (
  id: string,
  createSocialAccountDto: BodyType<CreateSocialAccountDto>,
  signal?: AbortSignal,
) => {
  return customInstance<SocialAccountDto>({
    url: `/api/v1/workspaces/${id}/social-accounts`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createSocialAccountDto,
    signal,
  });
};

export const getCreateSocialAccountMutationKey = () => ['createSocialAccount'] as const;

export const getCreateSocialAccountMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSocialAccount>>,
    TError,
    CreateSocialAccountMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSocialAccount>>,
  TError,
  CreateSocialAccountMutationVariables,
  TContext
> => {
  const mutationKey = getCreateSocialAccountMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSocialAccount>>,
    CreateSocialAccountMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createSocialAccount(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSocialAccountMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSocialAccount>>
>;
export type CreateSocialAccountMutationBody = BodyType<CreateSocialAccountDto>;
export type CreateSocialAccountMutationError = ErrorType<ApiErrorDto>;
export type CreateSocialAccountMutationVariables = {
  id: string;
  data: BodyType<CreateSocialAccountDto>;
};

/**
 * @summary Add an account for the brand or one of its personas.
 */
export const useCreateSocialAccount = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSocialAccount>>,
      TError,
      CreateSocialAccountMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createSocialAccount>>,
  TError,
  CreateSocialAccountMutationVariables,
  TContext
> => {
  return useMutation(getCreateSocialAccountMutationOptions(options), queryClient);
};
/**
 * @summary Change an account's handle or who it posts for.
 */
export const updateSocialAccount = (
  id: string,
  accountId: string,
  updateSocialAccountDto: BodyType<UpdateSocialAccountDto>,
  signal?: AbortSignal,
) => {
  return customInstance<SocialAccountDto>({
    url: `/api/v1/workspaces/${id}/social-accounts/${accountId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateSocialAccountDto,
    signal,
  });
};

export const getUpdateSocialAccountMutationKey = () => ['updateSocialAccount'] as const;

export const getUpdateSocialAccountMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateSocialAccount>>,
    TError,
    UpdateSocialAccountMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateSocialAccount>>,
  TError,
  UpdateSocialAccountMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateSocialAccountMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateSocialAccount>>,
    UpdateSocialAccountMutationVariables
  > = (props) => {
    const { id, accountId, data } = props ?? {};

    return updateSocialAccount(id, accountId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateSocialAccountMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateSocialAccount>>
>;
export type UpdateSocialAccountMutationBody = BodyType<UpdateSocialAccountDto>;
export type UpdateSocialAccountMutationError = ErrorType<ApiErrorDto>;
export type UpdateSocialAccountMutationVariables = {
  id: string;
  accountId: string;
  data: BodyType<UpdateSocialAccountDto>;
};

/**
 * @summary Change an account's handle or who it posts for.
 */
export const useUpdateSocialAccount = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateSocialAccount>>,
      TError,
      UpdateSocialAccountMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateSocialAccount>>,
  TError,
  UpdateSocialAccountMutationVariables,
  TContext
> => {
  return useMutation(getUpdateSocialAccountMutationOptions(options), queryClient);
};
/**
 * @summary Remove an account.
 */
export const deleteSocialAccount = (
  id: string,
  accountId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/social-accounts/${accountId}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeleteSocialAccountMutationKey = () => ['deleteSocialAccount'] as const;

export const getDeleteSocialAccountMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSocialAccount>>,
    TError,
    DeleteSocialAccountMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSocialAccount>>,
  TError,
  DeleteSocialAccountMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteSocialAccountMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSocialAccount>>,
    DeleteSocialAccountMutationVariables
  > = (props) => {
    const { id, accountId } = props ?? {};

    return deleteSocialAccount(id, accountId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteSocialAccountMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSocialAccount>>
>;

export type DeleteSocialAccountMutationError = ErrorType<ApiErrorDto>;
export type DeleteSocialAccountMutationVariables = { id: string; accountId: string };

/**
 * @summary Remove an account.
 */
export const useDeleteSocialAccount = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteSocialAccount>>,
      TError,
      DeleteSocialAccountMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteSocialAccount>>,
  TError,
  DeleteSocialAccountMutationVariables,
  TContext
> => {
  return useMutation(getDeleteSocialAccountMutationOptions(options), queryClient);
};
/**
 * @summary Read the account's public numbers now, whatever the last read said: a
handle marked not found or failed is asked about afresh.
 */
export const checkSocialAccount = (
  id: string,
  accountId: string,
  signal?: AbortSignal,
) => {
  return customInstance<AccountCheckDto>({
    url: `/api/v1/workspaces/${id}/social-accounts/${accountId}/check`,
    method: 'POST',
    signal,
  });
};

export const getCheckSocialAccountMutationKey = () => ['checkSocialAccount'] as const;

export const getCheckSocialAccountMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkSocialAccount>>,
    TError,
    CheckSocialAccountMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof checkSocialAccount>>,
  TError,
  CheckSocialAccountMutationVariables,
  TContext
> => {
  const mutationKey = getCheckSocialAccountMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof checkSocialAccount>>,
    CheckSocialAccountMutationVariables
  > = (props) => {
    const { id, accountId } = props ?? {};

    return checkSocialAccount(id, accountId);
  };

  return { mutationFn, ...mutationOptions };
};

export type CheckSocialAccountMutationResult = NonNullable<
  Awaited<ReturnType<typeof checkSocialAccount>>
>;

export type CheckSocialAccountMutationError = ErrorType<ApiErrorDto>;
export type CheckSocialAccountMutationVariables = { id: string; accountId: string };

/**
 * @summary Read the account's public numbers now, whatever the last read said: a
handle marked not found or failed is asked about afresh.
 */
export const useCheckSocialAccount = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof checkSocialAccount>>,
      TError,
      CheckSocialAccountMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof checkSocialAccount>>,
  TError,
  CheckSocialAccountMutationVariables,
  TContext
> => {
  return useMutation(getCheckSocialAccountMutationOptions(options), queryClient);
};
